import {Optional} from '@othree.io/optional'
import {marshall} from '@aws-sdk/util-dynamodb'
import {
    AwsCustomResource,
    AwsCustomResourcePolicy,
    AwsCustomResourceProps,
    AwsSdkCall,
    PhysicalResourceId,
} from 'aws-cdk-lib/custom-resources'
import {PolicyStatement, Role, ServicePrincipal} from 'aws-cdk-lib/aws-iam'
import {Duration, Stack} from 'aws-cdk-lib'

export type LambdaSdkCallInput = Readonly<{
    payload: any
    functionArn: string
    /** Arbitrary string injected into the payload to force re-invocation on redeploy. */
    hash: string
}>

export type DynamoWriteCallInput = Readonly<{
    tableName: string
    tableArn: string
    name: string
    /** Items to write. Maximum 25 (DynamoDB BatchWriteItem limit). */
    items: Array<any>
}>

/** An AWS SDK call paired with the IAM policy statement needed to execute it. */
export type AwsSdkCallOutput = Readonly<{
    awsSdkCall: AwsSdkCall
    policyStatement: PolicyStatement
}>

/** Builds an SDK call that invokes a Lambda function, with a scoped IAM policy. */
export const withLambdaSdkCall = (input: LambdaSdkCallInput): AwsSdkCallOutput => {
    const lambdaSdkCall: AwsSdkCall = {
        service: 'Lambda',
        action: 'invoke',
        parameters: {
            FunctionName: input.functionArn,
            Payload: JSON.stringify({
                ...input.payload,
                IgnoredHash: input.hash
            })
        },
        physicalResourceId: PhysicalResourceId.of(input.functionArn)
    }

    const policyStatement = new PolicyStatement({
        resources: [input.functionArn],
        actions: ['lambda:InvokeFunction']
    })

    return {
        awsSdkCall: lambdaSdkCall,
        policyStatement: policyStatement
    }
}

/** Marshalls items into DynamoDB PutRequest format. */
const buildDynamoPutItems = (items: Array<any>) => {
    return items.map( item => {
        return {
            PutRequest: {
                Item: marshall(item)
            }
        }
    })
}

/**
 * Builds an SDK call for a DynamoDB BatchWriteItem operation, with a scoped IAM policy.
 * Throws if items exceed the 25-item BatchWriteItem limit.
 */
export const withDynamoBatchWriteCall = (input: DynamoWriteCallInput): AwsSdkCallOutput => {

    if(input.items.length > 25){
        throw new Error('Batch write items length should be 25 or less')
    }

    const dynamoBatchWriteSdkCall = {
        service: 'DynamoDB',
        action: 'batchWriteItem',
        parameters: {
            RequestItems: {
                [input.tableName]: buildDynamoPutItems(input.items)
            },
        },
        physicalResourceId: PhysicalResourceId.of(input.name)
    }

    const policyStatement = new PolicyStatement({
        resources: [input.tableArn],
        actions: ['dynamodb:BatchWriteItem']
    })

    return {
        awsSdkCall: dynamoBatchWriteSdkCall,
        policyStatement: policyStatement
    }
}

/** Custom resource configuration requiring an explicit IAM policy. */
export type CustomResourceConfiguration = Partial<AwsCustomResourceProps> & Readonly<{
    policy: AwsCustomResourcePolicy
}>

/** Returns a default configuration with a 10-minute timeout and a broad SDK-calls policy. */
export const withDefaultCustomResourceConfiguration = (): CustomResourceConfiguration => {
    return {
        policy: AwsCustomResourcePolicy.fromSdkCalls({resources: AwsCustomResourcePolicy.ANY_RESOURCE}),
        timeout: Duration.minutes(10)
    }
}

export type CustomResourceInput = Readonly<{
    name: string
}>

/**
 * Creates a CloudFormation custom resource backed by an AWS SDK call.
 * Supports onCreate, onUpdate, and onDelete lifecycle hooks, each with their own scoped IAM policy.
 */
export const withCustomResource = (deps: {
    stack: Stack
    customResourceConfiguration: CustomResourceConfiguration
    onCreateSdkCall?: AwsSdkCallOutput
    onUpdateSdkCall?: AwsSdkCallOutput
    onDeleteSdkCall?: AwsSdkCallOutput
}) =>
    (input: CustomResourceInput, configuration?: Partial<AwsCustomResourceProps>): AwsCustomResource => {
        const awsCustomResourceRole = new Role(deps.stack,
            `${input.name}-Role`,
            {
                assumedBy: new ServicePrincipal('lambda.amazonaws.com')
            }
        )

        Optional(deps.onCreateSdkCall).map(_ => awsCustomResourceRole.addToPolicy(_.policyStatement))
        Optional(deps.onUpdateSdkCall).map(_ => awsCustomResourceRole.addToPolicy(_.policyStatement))
        Optional(deps.onDeleteSdkCall).map(_ => awsCustomResourceRole.addToPolicy(_.policyStatement))

        const initializers: Partial<AwsCustomResourceProps> = {
            ...Optional(deps.onCreateSdkCall).map(_ => ({
                onCreate: deps.onCreateSdkCall!.awsSdkCall
            })).orElse(undefined!),
            ...Optional(deps.onUpdateSdkCall).map(_ => ({
                onUpdate: deps.onUpdateSdkCall!.awsSdkCall
            })).orElse(undefined!),
            ...Optional(deps.onDeleteSdkCall).map(_ => ({
                onDelete: deps.onDeleteSdkCall!.awsSdkCall
            })).orElse(undefined!)
        }

        const awsCustomResource = new AwsCustomResource(deps.stack, input.name, {
            ...deps.customResourceConfiguration,
            ...initializers,
            role: awsCustomResourceRole,
            ...Optional(configuration).orElse({}),
            functionName: `${input.name}-Handler`
        })

        return awsCustomResource
    }
